/**
 * CommandPalette.tsx — Sidebar P2c (Wave 2, 2026-05-21).
 *
 * Cmd+P / Ctrl+P opens a route-jump modal. NOT Cmd+K — that's reserved
 * for Ask Nexus (chat). v1 scope: route-jump only. Future Wave 3+ can
 * extend with action-search ("create invoice"), entity switch, recents.
 *
 * Behavior:
 *   - Open via global keydown (useKeyboardShortcuts.ts)
 *   - Close via Esc / backdrop click / route selection
 *   - Type to filter (substring + token AND match, label-priority ranking)
 *   - Arrow up/down to navigate results, Enter to commit
 *   - Role-filtered (viewer doesn't see /admin, manager doesn't see /finance)
 *   - Focus trap (input captures Tab; modal captures focus)
 *
 * Accessibility:
 *   - role="dialog" + aria-modal="true"
 *   - aria-label="Command palette"
 *   - Result list has role="listbox", items role="option" with aria-selected
 *   - aria-activedescendant points to highlighted item
 *
 * Mounted globally via App.tsx. Visibility controlled by useState in
 * App.tsx that toggles open() / close().
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '@/hooks/useRole';
import { useAppStore } from '@/stores/useAppStore';
import {
  flattenLayoutForPalette,
  filterByRole,
  filterManagerOnlyGroups,
  filterAndRankRoutes,
  TASK_LAYOUT,
} from '@/lib/command-palette-routes';
import { Search, X, ArrowRight } from 'lucide-react';

const MAX_RESULTS = 8;

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { hasMinRole, isManager, isAdmin } = useRole();
  const activeEntity = useAppStore((s) => s.activeEntity);
  const isMultiEntity = useAppStore((s) => s.entities.length > 1);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = 'command-palette-listbox';

  // Stable route catalog. Recomputes only when entity-count crosses 1/many
  // boundary (Consolidated route visibility).
  const allRoutes = useMemo(
    () => flattenLayoutForPalette(TASK_LAYOUT, { isMultiEntity }),
    [isMultiEntity],
  );

  // Role-filtered subset. Recomputes when role changes (entity switch).
  // hasMinRole expects a non-undefined UserRole; undefined minRole means
  // "no gate — visible to all", which we represent as 'viewer' (the lowest
  // level any non-employee/non-manager user has). employee + manager
  // already get the SideNav redirect to /employee or /manager/approvals
  // so route-jumping to /finance/* from this palette stays UX-only — the
  // backend role gate is the actual security boundary.
  // Comprehensive-sweep audit fix: filterManagerOnlyGroups closes a gap
  // filterByRole alone can't — MGR_SCHEDULE/MGR_APPROVALS/etc. all have
  // minRole:'manager' (level 5), which hasMinRole() satisfies for every
  // role except employee, so bookkeeper/accountant/viewer would otherwise
  // see "Team schedule" as a clickable result that dead-ends on
  // ManagerRouteGuard's permission screen. See that function's docblock.
  const visibleRoutes = useMemo(
    () => filterManagerOnlyGroups(
      filterByRole(allRoutes, (minRole) => hasMinRole(minRole ?? 'viewer')),
      isManager || isAdmin,
    ),
    [allRoutes, hasMinRole, isManager, isAdmin],
  );

  // Filtered + ranked results for the current query.
  const results = useMemo(
    () => filterAndRankRoutes(visibleRoutes, query, MAX_RESULTS),
    [visibleRoutes, query],
  );

  // Reset query + highlight when the palette transitions to open, and clamp
  // highlightIndex whenever the result count shrinks out from under it
  // (e.g. a role/entity switch while the palette is open with a query
  // already typed — onChange already resets highlightIndex on every
  // keystroke, so this only fires for non-typing-driven result changes).
  //
  // Lint fix: both used to be separate `useEffect` hooks calling setState
  // directly in the body, which react-hooks/set-state-in-effect flags —
  // that pattern causes an extra "stale then corrected" render pass (open
  // with old query -> effect fires post-render -> setState -> re-render).
  // Rewritten as the React-documented "adjust state during render" pattern
  // (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes):
  // compare against a tracked previous value INSIDE the render body and
  // call setState conditionally there, which React explicitly supports and
  // applies within the same render pass — same end-state as the old
  // effects, one fewer render, no lint violation.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setQuery('');
      setHighlightIndex(0);
    }
  }
  const [prevResultsLength, setPrevResultsLength] = useState(results.length);
  if (results.length !== prevResultsLength) {
    setPrevResultsLength(results.length);
    if (highlightIndex >= results.length && results.length > 0) {
      setHighlightIndex(results.length - 1);
    }
  }

  // Focus is a genuine external-system side effect (imperative DOM call),
  // not derived state, so it stays in a real effect.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Listen for global Escape (custom event from useKeyboardShortcuts).
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = () => onClose();
    window.addEventListener('nexus:escape', handleEscape);
    return () => window.removeEventListener('nexus:escape', handleEscape);
  }, [isOpen, onClose]);

  // Key handlers — Tab + arrows + Enter — scoped to the palette only.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[highlightIndex];
      if (target) {
        navigate(target.path);
        onClose();
      }
    } else if (e.key === 'Tab') {
      // Trap focus inside the palette. Only one focusable element (input);
      // Tab is a no-op so focus can't escape to the page underneath.
      e.preventDefault();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-white dark:bg-slate-800 shadow-2xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <Search className="w-5 h-5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlightIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Jump to a page…"
            className="flex-1 bg-transparent outline-none text-base text-slate-900 dark:text-white placeholder:text-slate-400"
            aria-controls={listboxId}
            aria-activedescendant={
              results.length > 0 ? `${listboxId}-option-${highlightIndex}` : undefined
            }
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            aria-label="Close palette"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Result list */}
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Matching pages"
          className="max-h-[400px] overflow-y-auto"
        >
          {results.length === 0 && (
            <li className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              {query.trim() ? `No matches for "${query}"` : 'Start typing to search…'}
            </li>
          )}
          {results.map((r, i) => {
            const Icon = r.icon;
            const isHighlighted = i === highlightIndex;
            return (
              <li
                id={`${listboxId}-option-${i}`}
                key={r.path}
                role="option"
                aria-selected={isHighlighted}
                className={[
                  'flex items-center gap-3 px-4 py-2.5 cursor-pointer text-sm',
                  isHighlighted
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700',
                ].join(' ')}
                onClick={() => {
                  navigate(r.path);
                  onClose();
                }}
                onMouseEnter={() => setHighlightIndex(i)}
              >
                <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate font-medium">{r.label}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{r.group}</span>
                {isHighlighted && (
                  <ArrowRight className="w-4 h-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ul>

        {/* Footer hint */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 dark:text-slate-400">
          <span>
            {activeEntity?.legalName ? `In ${activeEntity.legalName}` : 'No entity'}
          </span>
          <span className="flex items-center gap-3">
            <span><kbd className="px-1 rounded bg-slate-100 dark:bg-slate-700">↑↓</kbd> navigate</span>
            <span><kbd className="px-1 rounded bg-slate-100 dark:bg-slate-700">Enter</kbd> open</span>
            <span><kbd className="px-1 rounded bg-slate-100 dark:bg-slate-700">Esc</kbd> close</span>
          </span>
        </div>
      </div>
    </div>
  );
}
